import 'whatwg-fetch'; /*global fetch*/
import _ from 'underscore';
import {log} from './logging';

export const DEFAULT_AUCTIONS_PER_PAGE = 20;
export const AUCTIONEERS_PER_PAGE = 12;

// API Response Cache
var apiCache = {};
var CACHE_TTL_MS = 60000; // 1 minute cache TTL

export var getCacheKey = function(endpoint) {
    return endpoint;
};

export var getCachedResponse = function(endpoint) {
    var key = getCacheKey(endpoint);
    var cached = apiCache[key];

    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        log('info', 'Cache HIT', key);
        return cached.data;
    }

    if (cached) {
        log('info', 'Cache EXPIRED', key);
        delete apiCache[key];
    }

    return null;
};

export var setCachedResponse = function(endpoint, data) {
    var key = getCacheKey(endpoint);
    apiCache[key] = {
        data: data,
        timestamp: Date.now()
    };
    log('info', 'Cache SET', key);
};

export var clearCache = function() {
    apiCache = {};
    log('info', 'Cache CLEARED');
};

// Prefetch queue to prevent duplicate requests
var pendingRequests = {};

export const optionalUrlSegment = (segment) => segment ? `${segment}/` : '';

const checkStatus = (response) => {
    let {status, statusText} = response;

    if (status >= 200 && status < 300) {
        return response;
    } else {
        let error = new Error(statusText);

        error.response = response;
        throw error;
    }
};

const parseJSON = (response) => response.json();

let apiUrl = 'https://auctionlookapp.azurewebsites.net/api/';
export const _setApiUrl = (newApiUrl) => apiUrl = newApiUrl;

export const fetchFromApi = function(endpoint, options) {
    options = options || {};

    if (!apiUrl) {
        throw new Error('You must set an API url using _setApiUrl from this module');
    }

    // Check cache first for GET requests
    var isGetRequest = !options.method || options.method === 'GET';
    if (isGetRequest) {
        var cached = getCachedResponse(endpoint);
        if (cached) {
            return Promise.resolve(cached);
        }

        // Check if request is already in flight
        if (pendingRequests[endpoint]) {
            log('info', 'Request already pending', endpoint);
            return pendingRequests[endpoint];
        }
    }

    var requestPromise = fetch(
        apiUrl + endpoint,
        {
            method: 'GET',
            ...options,
            headers: {
                'Accept': 'application/json',
                ...(options.headers || {})
            }
        }
    )
    .then(checkStatus)
    .then(parseJSON)
    .then(function(data) {
        // Cache the response
        if (isGetRequest) {
            setCachedResponse(endpoint, data);
            delete pendingRequests[endpoint];
        }
        return data;
    })
    .catch(function(error) {
        if (isGetRequest) {
            delete pendingRequests[endpoint];
        }
        throw error;
    });

    // Track pending request
    if (isGetRequest) {
        pendingRequests[endpoint] = requestPromise;
    }

    return requestPromise;
};

// Prefetch without blocking - returns immediately, caches in background
export var prefetchFromApi = function(endpoint) {
    // Don't prefetch if already cached or pending
    var cached = getCachedResponse(endpoint);
    if (cached || pendingRequests[endpoint]) {
        return;
    }

    log('info', 'Prefetching', endpoint);
    fetchFromApi(endpoint).catch(function(err) {
        log('info', 'Prefetch failed', endpoint, err);
    });
};

export const formatQuerystring = (keyValues) => {
    let args = _.chain(keyValues)
        .mapObject((value, key) => (
            key && value && `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        ))
        .compact()
        .value();
    
    return args.length
        ? '?' + args.join('&')
        : '';
};

export const makeRaceProtectionContext = () => {
    let _requestIds = {};
    const _generateRequestId = (key) => _requestIds[key]
        ? ++_requestIds[key]
        : (_requestIds[key] = 1);
    const _getRequestId = (key) => _requestIds[key] || 0;
    const makeSuccessCallbackWithRaceProtection = (key, dispatch, successAction) => {
        let thisRequestId = _generateRequestId(key);
       
        return (data) => {
            let currentRequestId = _getRequestId(key);
            let requestInfo = `(request ${key} ${thisRequestId}/${currentRequestId})`;
    
            if (currentRequestId === thisRequestId) {
                log(
                    'info',
                    'Keeping non-stale data',
                    requestInfo
                );
                dispatch(successAction(data));
            } else {
                log(
                    'info',
                    'Discarded stale request data',
                    requestInfo,
                    data
                );
            }
       };
    };
    return makeSuccessCallbackWithRaceProtection;
}